import React, { useState, useEffect, useRef } from 'react'
import { DragDropContext } from 'react-beautiful-dnd'
import * as GridTemplateParser from 'grid-template-parser'
import Classnames from 'classnames'
import _ from 'lodash'
import produce from 'immer'
import { motion } from 'framer-motion'
import axios from 'axios'
import { reactLocalStorage } from 'reactjs-localstorage'
import Cookies from 'js-cookie'
import { Spinner } from 'baseui/spinner'
import { useRecoilState, useRecoilValue, useRecoilCallback } from 'recoil'
import { opTitle, opDesc } from '@/utils/store/onepager_template'
import { layoutIds, layout } from '@/store/template/layout'
import { sectionIds, section } from '@/store/template/section'
import {
  content,
  content_block,
  content_loaded,
} from '@/store/template/content'
import { publicIsLoggedIn, currentTemplateID } from '@/store/onepager/onepager'
import { design } from '@/store/template/design'
import {
  grid_matrix,
  grid_rows,
  grid_columns,
  grid_area,
  total_boxes,
} from '@/store/template/settings'

// onepager
import LayoutBox from '@/components/onepager/Layout'

function PublicGrid({ onepager, collection }) {
  const [layouts] = useRecoilState(layoutIds)
  const [gridMatrix, setGridMatrix] = useRecoilState(grid_matrix)
  const gridRows = useRecoilValue(grid_rows)
  const gridColumns = useRecoilValue(grid_columns)
  const gridTotalBoxes = useRecoilValue(total_boxes)
  const gridArea = useRecoilValue(grid_area)
  const getDesign = useRecoilValue(design)
  const getOpTitle = useRecoilValue(opTitle)
  const [areas, setAreas] = useState()
  const [getGridParsed, setGridParsed] = useState()
  const isLoggedIn = useRecoilValue(publicIsLoggedIn)
  const getCurrentTemplateId = useRecoilValue(currentTemplateID)
  const [showContentLoader, setShowContentLoader] = useState(false)

  // Get User Content
  useEffect(async () => {
    // IF SHARE ID - LOAD THAT
    if (findGetParameter('shareId')) {
      // grab share user content
      setShowContentLoader(true)
      const getUserContent = await axios.post(
        '/api/rest/get-user-content-by-id',
        {
          contentId: findGetParameter('shareId'),
        }
      )
      if (getUserContent.data) {
        // only refresh if content is not empty
        if (getUserContent.data.content.length > 0) {
          refreshStore(getUserContent.data)
        } else {
          // does not have content - malformed content
        }
      }
      setShowContentLoader(false)
    } else {
      if (isLoggedIn) {
        // check if logged in with authed=true or wl-email
        if (findGetParameter('autosignup')) {
          // auto sign up in url
          const signUpAndCheckContent = await axios.post(
            '/api/rest/signup-email-and-get-content',
            {
              email: findGetParameter('autosignup'),
              collectionId: collection._id,
              templateId: getCurrentTemplateId,
              webhook: collection.webhook_url,
            }
          )
          // only refresh if content is not empty
          if (
            signUpAndCheckContent.data &&
            signUpAndCheckContent.data.content
          ) {
            if (signUpAndCheckContent.data.content.length > 0) {
              refreshStore(signUpAndCheckContent.data)
            }
          }
        } else if (Cookies.get('wl-email')) {
          setShowContentLoader(true)

          let email = Cookies.get('wl-email')
          const getUserContent = await axios.post(
            '/api/rest/get-email-content',
            {
              email: email,
              templateId: getCurrentTemplateId,
            }
          )

          // if above failed then check other way for user data
          if (!getUserContent.data) {
            const getUserContentCheck = await axios.post(
              '/api/rest/get-user-content-id',
              {
                email: email,
                templateId: getCurrentTemplateId,
              }
            )

            if (getUserContentCheck.data.contentId) {
              const getUserContentCheckContents = await axios.post(
                '/api/rest/get-user-content-by-id',
                {
                  contentId: getUserContentCheck.data.contentId['@ref'].id,
                }
              )
              if (getUserContentCheckContents.data) {
                refreshStore(getUserContentCheckContents.data)
              }
            }
          }

          if (getUserContent.data && getUserContent.data.content) {
            // only refresh if content is not empty

            if (getUserContent.data.content.length > 0) {
              refreshStore(getUserContent.data)
            }
          }

          setShowContentLoader(false)
        }
      } else {
        setShowContentLoader(true)
        // IF NOT LOGGED IN - USE LOCAL STORAGE
        if (reactLocalStorage.get(getCurrentTemplateId)) {
          refreshStore(JSON.parse(reactLocalStorage.get(getCurrentTemplateId)))
        }
        setShowContentLoader(false)
      }
    }
  }, [getOpTitle])

  function findGetParameter(parameterName) {
    var result = null,
      tmp = []
    location.search
      .substr(1)
      .split('&')
      .forEach(function (item) {
        tmp = item.split('=')
        if (tmp[0] === parameterName) result = decodeURIComponent(tmp[1])
      })

    return result
  }

  const refreshStore = useRecoilCallback(
    ({ template, set, snapshot }) =>
      async template => {
        // build contents
        // let allContentIds = []
        _.forEach(template.content, function (contentData) {
          // allContentIds.push(contentData.id)
          set(content(contentData.id), contentData.atom)
        })
        // set(contentIds, allContentIds)

        //build content_blocks
        _.forEach(template.content_blocks, function (contentBlockData) {
          set(content_block(contentBlockData.id), contentBlockData.atom)
        })
      }
  )

  const setContentLoaded = useRecoilCallback(
    ({ template, set }) =>
      template => {
        // set content_loaded true
        set(content_loaded, true)
      }
  )

  let _GridTemplateParser = GridTemplateParser
  let template = _GridTemplateParser.template
  let area = _GridTemplateParser.area
  let gridParser = _GridTemplateParser.grid

  useEffect(async () => {
    // set grid rows / columns
    setGrid(gridMatrix)
  }, [])

  // set grid JS representation
  function setGrid() {
    let gridLayout = {}
    let columnsArray = setColumnsArray(gridRows, gridColumns)
    let globalRow = 0

    // set base grid
    for (var i = 0; i < gridTotalBoxes; i++) {
      // vars
      let setIndex = i + 1
      let column = i

      const columndEndCheck = columnsArray.filter(x => x == setIndex - 1)
      if (columndEndCheck[0]) {
        globalRow = globalRow + 1
      }

      if (globalRow > 0) {
        column = column - columnsArray[globalRow - 1]
      }

      gridLayout['opB' + setIndex] = area({
        x: column,
        y: globalRow,
        width: 1,
        height: 1,
      })
    }

    const areasCompute = template({
      width: gridColumns,
      height: gridRows,
      areas: gridLayout,
    })

    setAreas(areasCompute)
    setGridParsed(gridParser(areasCompute))
  }

  function setColumnsArray(row, column) {
    let array = []
    for (var i = 0; i < row; i++) {
      if (i > 0) {
        let num = i * parseInt(column)
        array.push(num)
      }
    }
    return array
  }

  const onDragEnd = result => {
    const { source, destination } = result

    // drops outside of a list - return to normal
    if (!destination) {
      return
    }

    // moved back to same spot
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return
    }

    const sInd = source.droppableId
    const dInd = destination.droppableId

    if (sInd === dInd) {
      // reorder same section
      const { items, contentID } = allListItems(sInd)
      const newItemsOrder = reorder(items, source.index, destination.index)
      updateListOrder(contentID, newItemsOrder)
    } else {
      // move between
      const { items: sourceItems, contentID: sourceContentID } =
        allListItems(sInd)
      const { items: destItems, contentID: destContentID } = allListItems(dInd)
      const result = move(sourceItems, destItems, source, destination)

      updateListOrder(sourceContentID, result[sInd])
      updateListOrder(destContentID, result[dInd])
    }
  }

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const move = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source)
    const destClone = Array.from(destination)
    const [removed] = sourceClone.splice(droppableSource.index, 1)

    destClone.splice(droppableDestination.index, 0, removed)

    const result = {}
    result[droppableSource.droppableId] = sourceClone
    result[droppableDestination.droppableId] = destClone

    return result
  }

  // get all items
  const allListItems = useRecoilCallback(({ snapshot }) => sectionID => {
    // get content ID
    const getThisSection = snapshot.getLoadable(section(sectionID)).contents
    const getThisContent = snapshot.getLoadable(
      content(getThisSection.contentID)
    ).contents

    return {
      type: getThisSection.type.toUpperCase(),
      items: getThisContent.blocks,
      contentID: getThisSection.contentID,
    }
  })

  // update new order list items
  const updateListOrder = useRecoilCallback(
    ({ snapshot, set }) =>
      (contentID, items) => {
        const getThisContent = snapshot.getLoadable(content(contentID)).contents

        const nextState = produce(getThisContent, draft => {
          draft.blocks = items
        })

        set(content(contentID), nextState)
      }
  )

  return (
    <div className="">
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.4 }}
        variants={{
          hidden: {
            // opacity: 0,
            // scale: 0.9,
            // y: 50,
          },
          visible: {
            // opacity: 1,
            // scale: 1,
            // y: 0,
          },
          exit: {
            // opacity: 0,
            // scale: 0.9,
            // y: 50,
          },
        }}
        className={Classnames(
          'gridWrapper container  mx-auto z-10 relative lg:px-5 xl:px-0',
          {
            'max-w-7xl': !getDesign.canvas_width || getDesign.canvas_width == 7,
            'max-w-6xl': getDesign.canvas_width == 6,
            'max-w-5xl': getDesign.canvas_width == 5,
            'max-w-4xl': getDesign.canvas_width == 4,
            floatingGridSimple: getDesign.canvas_style == 'floating',
            boldSectionTitle: getDesign.section_title_font == 'bold',
            sectionWhiteText: getDesign.section_text_color == 'white',
            sectionTopLevelBGColor:
              getDesign.section_bg_color &&
              getDesign.section_bg_color !== 'rgb(255, 255, 255)',
            noHeaderGrid: getDesign.header_layout == 'noheader',
          }
        )}
        style={{ marginTop: '-260px' }}
      >
        {/* Loading screen  */}
        {showContentLoader && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
            variants={{
              hidden: {
                opacity: 0,
                scale: 0.98,
              },
              visible: {
                opacity: 1,
                scale: 1,
              },
              exit: {
                opacity: 0,
                scale: 0.98,
              },
            }}
            className="absolute top-0 left-0 h-full w-full bg-white bg-opacity-90 z-30 flex  justify-center loadingGridContent"
          >
            <div className="mt-10 bg-white rounded shadow-xl loadingGridContentInner flex flex-col items-center  justify-center">
              <div>
                <Spinner size="75px" color="#ffc900" />
              </div>
              <div className="text-xl font-bold mt-5">Loading...</div>
            </div>
          </motion.div>
        )}

        <div className="gridWrapper gridWrapperPublic z-10 gridStyleRoundedApart">
          <div
            className={Classnames(
              'boxes lg:grid',
              'grid-container-skeleton',
              'builder',
              'public-grid-wrapper',
              {
                [`grid_${gridMatrix}`]: true,
              }
            )}
            style={{
              gridTemplateColumns: 'repeat(' + gridColumns + ', 1fr)',
              gridTemplateAreas: areas,
            }}
          >
            <DragDropContext onDragEnd={onDragEnd}>
              {layouts.map((layoutID, index) => (
                <LayoutBox key={layoutID} layoutID={layoutID} index={index} />
              ))}
            </DragDropContext>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default PublicGrid
